import { cn } from '@/lib/tailwind.utils'
import { SUITABILITY_GRADE_LABELS, SuitabilityGrade } from '../types'

type BadgeSize = 'xs' | 'sm' | 'md' | 'lg'

interface SuitabilityBadgeConfig {
  className: string
  dotClassName: string
  label: string
}

const gradeConfigMap: Record<SuitabilityGrade, SuitabilityBadgeConfig> = {
  A: {
    className: 'bg-green-100 text-green-700',
    dotClassName: 'bg-green-700',
    label: SUITABILITY_GRADE_LABELS.A,
  },
  B: {
    className: 'bg-green-50 text-green-500',
    dotClassName: 'bg-green-500',
    label: SUITABILITY_GRADE_LABELS.B,
  },
  C: {
    className: 'bg-amber-50 text-amber-600',
    dotClassName: 'bg-amber-600',
    label: SUITABILITY_GRADE_LABELS.C,
  },
  D: {
    className: 'bg-orange-50 text-orange-700',
    dotClassName: 'bg-orange-700',
    label: SUITABILITY_GRADE_LABELS.D,
  },
  E: {
    className: 'bg-red-50 text-red-800',
    dotClassName: 'bg-red-800',
    label: SUITABILITY_GRADE_LABELS.E,
  },
}

const fallbackConfig: SuitabilityBadgeConfig = {
  className: 'bg-muted text-muted-foreground',
  dotClassName: 'bg-muted-foreground',
  label: '?',
}

const sizeClassName: Record<BadgeSize, string> = {
  xs: 'px-1.5 py-0.5 gap-1 text-[10px] rounded',
  sm: 'px-2 py-0.5 gap-1.5 text-xs rounded',
  md: 'px-2.5 py-1 gap-1.5 text-sm rounded-md',
  lg: 'px-3 py-1.5 gap-2 text-base rounded-md',
}

const dotSizeClassName: Record<BadgeSize, string> = {
  xs: 'size-1',
  sm: 'size-1.5',
  md: 'size-2',
  lg: 'size-2.5',
}

type SuitabilityBadgeProps = (
  | { grade: SuitabilityBadgeConfig; letterGrade?: never }
  | { letterGrade: SuitabilityGrade; grade?: never }
) & {
  size?: BadgeSize
  label?: string
  showDot?: boolean
  className?: string
}

export const SuitabilityBadge = ({
  grade,
  letterGrade,
  size = 'sm',
  label,
  showDot = false,
  className,
}: SuitabilityBadgeProps) => {
  const resolvedGrade =
    grade ?? (letterGrade ? (gradeConfigMap[letterGrade] ?? fallbackConfig) : fallbackConfig)

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium',
        sizeClassName[size],
        resolvedGrade.className,
        className,
      )}
    >
      {showDot && (
        <span
          className={cn(
            'shrink-0 rounded-full',
            dotSizeClassName[size],
            resolvedGrade.dotClassName,
          )}
        />
      )}
      {label ?? resolvedGrade.label}
    </span>
  )
}
